import React, { useState, useMemo, useEffect, useRef } from 'react';
import { 
  FaBed, FaUsers, FaExpand, FaEye, FaWifi, FaCoffee, FaTv, FaShower, 
  FaCheck, FaTimes, FaInfoCircle,
  FaStar, FaCrown, FaLeaf, FaUmbrellaBeach, FaCity, FaMountain,
  FaSwimmingPool, FaParking, FaConciergeBell, FaDumbbell, FaChevronRight
} from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import hotelService from '../../services/hotelService';
import LoadingSpinner from '../LoadingSpinner';
import cartService from '../../services/cartService';
import travelAgentService from '../../services/travelAgentService';
import ProfessionalConfirmationPopup from '../ProfessionalConfirmationPopup';
import {
  buildAgentCommissionSnapshot,
  getAgentMarkupBreakdown,
  getDefaultCommissionPctForRateSource
} from '../../utils/agentCommissionCart';
import { calculateHotelProcessingFees } from '../../services/util/hotelProcessingFeesService';
import { useCart } from '../../contexts/CartContext';
import { getOffMarketSpecialCommissionBreakdown, OFF_MARKET_DEFAULT_COMMISSION_PCT } from '../../utils/offMarketSpecialCommission';

import { resolveRateSourceFromPlan, RATE_SOURCE_GDS as SABRE_GDS_RATE_SOURCE } from '../../utils/rateSource';

const isGdsRatePlan = (plan) =>
  String(plan?.rateSource ?? plan?.RateSource ?? SABRE_GDS_RATE_SOURCE) === SABRE_GDS_RATE_SOURCE;

const RoomExplorerSelection = ({ hotelCode, roomsData, loading, searchCriteria, hotelData, onRoomSelected, offMarketProperty = null }) => {
  const { refreshCartCount } = useCart();
  const [expandedRoomTypes, setExpandedRoomTypes] = useState({});
  const [expandedConfigurations, setExpandedConfigurations] = useState({});
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [priceCheckResponses, setPriceCheckResponses ] = useState(null);
  const [selectedRatePlan, setSelectedRatePlan] = useState(null);
  const [reserveLoading, setReserveLoading] = useState(false);
  const [priceCheckLoading, setPriceCheckLoading] = useState(false);
  const [pendingSelections, setPendingSelections] = useState([]);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [addToCartLoading, setAddToCartLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    hotelInfo: true,
    roomDetails: true,
    amenities: false,
    cancellation: false,
    pricing: true,
    guarantee: false,
    additionalInfo: false
  });
  const [emissionsData, setEmissionsData] = useState({});
  const [emissionsLoading, setEmissionsLoading] = useState({});
  const [emissionsError, setEmissionsError] = useState({});

  const [ratePlanEmissions, setRatePlanEmissions] = useState({});
  const [ratePlanEmissionsLoading, setRatePlanEmissionsLoading] = useState({});
  const [ratePlanEmissionsError, setRatePlanEmissionsError] = useState({});
  const [preloadingEmissions, setPreloadingEmissions] = useState(false);
  /** Right-hand selection drawer: opens when rooms are added; closes when cart empty; user can toggle anytime. */
  const [selectionDrawerOpen, setSelectionDrawerOpen] = useState(false);
  const prevSelectedCountRef = useRef(0);

  /** Loaded from profile: show base + markup on rate cards when enabled on Commission markup page. */
  const [agentMarkupSettings, setAgentMarkupSettings] = useState({
    loaded: true,
    // Default to enabled so UI doesn't disappear when profile fetch fails.
    markupEnabled: true,
    showRoomCommissionCalculation: true,
    markupPercentage: 10
  });

  /** Per-room markup overrides keyed by selectionId (${roomIndex}-${RateKey}). Local state only, resets on reload. */
  const [roomMarkupOverrides, setRoomMarkupOverrides] = useState({});

  /** Commission side panel state. */
  const [commissionPanel, setCommissionPanel] = useState({ open: false, room: null, ratePlan: null, selectionId: null });

  /** Temp markup % while editing in the side panel. */
  const [panelTempMarkup, setPanelTempMarkup] = useState(10);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await travelAgentService.getProfile();
        const d = res?.data;
        if (cancelled || !d) return;
        const isOM =
          Boolean(offMarketProperty) || Boolean(searchCriteria?.offMarketStay);
        const omBreakdown =
          offMarketProperty?.specialCommission?.enabled
            ? getOffMarketSpecialCommissionBreakdown(offMarketProperty.specialCommission)
            : null;
        const floor = omBreakdown?.total ?? (isOM ? OFF_MARKET_DEFAULT_COMMISSION_PCT : 10);
        const raw = d.markupPercentage;
        let pct;
        if (isOM) {
          pct = floor;
        } else {
          const n = raw === null || raw === undefined ? 10 : Number(raw);
          pct = Number.isNaN(n) ? 10 : Math.min(100, Math.max(10, n));
        }
        setAgentMarkupSettings({
          loaded: true,
          markupEnabled: true,
          showRoomCommissionCalculation: true,
          markupPercentage: pct
        });
      } catch {
        if (!cancelled) {
          setAgentMarkupSettings((prev) => ({
            ...prev,
            loaded: true,
            markupEnabled: true,
            showRoomCommissionCalculation: true
          }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [offMarketProperty, searchCriteria?.offMarketStay]);

  const showMarkupBreakdown =
    agentMarkupSettings.markupEnabled &&
    agentMarkupSettings.showRoomCommissionCalculation;

  const isOffMarketStay =
    Boolean(offMarketProperty) || Boolean(searchCriteria?.offMarketStay);

  const MAX_MARKUP = 100;
  const STEP = 0.1;

  /**
   * Off-market special commission breakdown for this hotel.
   * When the hotel has a special commission enabled, the slider minimum
   * is raised to specialCommission.percentage (e.g. 6.5%) and the UI
   * shows the hotel's add-on split.
   */
  const offMarketCommission = useMemo(
    () =>
      offMarketProperty?.specialCommission?.enabled
        ? getOffMarketSpecialCommissionBreakdown(offMarketProperty.specialCommission)
        : null,
    [offMarketProperty],
  );

  /** Commission floor: 10% GDS (100), 7% Booking.com (113), or off-market rules. */
  const getRateCommissionFloor = (ratePlan) => {
    if (offMarketCommission) return offMarketCommission.total;
    if (isOffMarketStay) return OFF_MARKET_DEFAULT_COMMISSION_PCT;
    return getDefaultCommissionPctForRateSource(ratePlan?.rateSource ?? ratePlan?.RateSource);
  };

  /** Default slider % for a rate (source-specific: 10% GDS, 7% Booking.com). */
  const getContextDefaultMarkup = (ratePlan) => {
    if (offMarketCommission) return offMarketCommission.total;
    if (isOffMarketStay) return OFF_MARKET_DEFAULT_COMMISSION_PCT;
    return getRateCommissionFloor(ratePlan);
  };

  /** Maximum markup rate: 100% for all stays. */
  const effectiveMaxMarkup = offMarketCommission ? 100 : MAX_MARKUP;

  const adjustPanelMarkup = (current, delta, floor) => {
    const next = Math.round((current + delta) * 10) / 10;
    return Math.min(effectiveMaxMarkup, Math.max(floor, next));
  };

  /**
   * Returns the effective markup % for a given selectionId + rate plan.
   * Always clamped to the rate-source floor.
   */
  const getEffectiveMarkup = (selectionId, ratePlan) => {
    const floor = getRateCommissionFloor(ratePlan);
    const raw =
      roomMarkupOverrides[selectionId] !== undefined
        ? roomMarkupOverrides[selectionId]
        : getContextDefaultMarkup(ratePlan);
    return Math.max(floor, raw);
  };

  /**
   * Incremental % added to the customer price for a given markup rate.
   * Baseline is the rate-source floor (7% Booking.com, 10% GDS, or off-market).
   */
  const getIncrementalPct = (pct, floor) =>
    Math.max(0, Math.round((pct - floor) * 10) / 10);

  /** Dollar amount added to the customer price. */
  const getIncrementalAmount = (base, pct, floor) =>
    Math.round(base * (getIncrementalPct(pct, floor) / 100) * 100) / 100;

  /** Total the agent pays (base + incremental add-on). */
  const getYouPay = (base, pct, floor) =>
    Math.round((base + getIncrementalAmount(base, pct, floor)) * 100) / 100;

  const openCommissionPanel = (room, ratePlan, selectionId) => {
    const floor = getRateCommissionFloor(ratePlan);
    setPanelTempMarkup(Math.max(floor, getEffectiveMarkup(selectionId, ratePlan)));
    setCommissionPanel({ open: true, room, ratePlan, selectionId });
  };

  const saveCommissionPanel = () => {
    const { selectionId } = commissionPanel;
    setRoomMarkupOverrides((prev) => ({ ...prev, [selectionId]: panelTempMarkup }));
    setCommissionPanel((p) => ({ ...p, open: false }));
  };

  const closeCommissionPanel = () => setCommissionPanel((p) => ({ ...p, open: false }));

  useEffect(() => {
    if (!commissionPanel.open) return;
    const handle = (e) => { if (e.key === 'Escape') closeCommissionPanel(); };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [commissionPanel.open]);

  useEffect(() => {
    const n = selectedRooms.length;
    const prev = prevSelectedCountRef.current;
    if (n > prev) {
      setSelectionDrawerOpen(true);
    }
    if (n === 0) {
      setSelectionDrawerOpen(false);
    }
    prevSelectedCountRef.current = n;
  }, [selectedRooms.length]);

  useEffect(() => {
    if (!selectionDrawerOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setSelectionDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectionDrawerOpen]);

  useEffect(() => {
    if (!selectionDrawerOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [selectionDrawerOpen]);

  // Process and group rooms by type
  const roomsByType = useMemo(() => {
    if (!roomsData || loading) return {};

    let rooms = [];
    
    // Extract rooms from various possible data structures
    if (roomsData?.rooms) {
      rooms = roomsData.rooms;
    } else if (roomsData?.GetHotelRateInfoRS?.HotelRateInfos?.HotelRateInfo?.[0]?.Rooms?.Room) {
      rooms = roomsData.GetHotelRateInfoRS.HotelRateInfos.HotelRateInfo[0].Rooms.Room;
    } else if (roomsData?.data?.GetHotelRateInfoRS?.HotelRateInfos?.HotelRateInfo?.[0]?.Rooms?.Room) {
      rooms = roomsData.data.GetHotelRateInfoRS.HotelRateInfos.HotelRateInfo[0].Rooms.Room;
    }

    if (!rooms || rooms.length === 0) return {};

    const groupedRooms = {};
    
    rooms.forEach((room, index) => {
      const roomTypeName = room.RoomType || room.RoomDescription?.Name || 'Standard Room';
      const bedInfo = room.BedTypeOptions?.BedTypes?.[0]?.BedType?.map(bed => 
        `${bed.Count || 1} ${bed.Description || 'Bed'}`
      ).join(', ') || 'Standard Bed';

      // Process rate plans — off-market stays: Sabre GDS (100) only
      const rawRatePlans = room.RatePlans?.RatePlan ? (
        Array.isArray(room.RatePlans.RatePlan)
          ? room.RatePlans.RatePlan
          : [room.RatePlans.RatePlan]
      ) : [];
      const ratePlans = isOffMarketStay
        ? rawRatePlans.filter(isGdsRatePlan)
        : rawRatePlans;

      // Enhanced rate plans with proper pricing
      const enhancedRatePlans = ratePlans.map((plan) => {
        const convertedRateInfo = plan.ConvertedRateInfo || {};
        const totalAmount = parseFloat(convertedRateInfo.AmountAfterTax || 0);
        const baseAmount = parseFloat(convertedRateInfo.AmountBeforeTax || totalAmount);
        const averageNightlyRate = parseFloat(convertedRateInfo.AverageNightlyRate || 0);
        const currency = convertedRateInfo.CurrencyCode || searchCriteria.currency || 'USD';

        const nights = hotelService.calculateNights(searchCriteria.checkInDate, searchCriteria.checkOutDate);
        const pricePerNight = averageNightlyRate || (nights > 0 ? totalAmount / nights : totalAmount);
        const basePerNight = nights > 0 ? baseAmount / nights : baseAmount;

        // Extract cancellation details
        const cancelPenalties = convertedRateInfo.CancelPenalties?.CancelPenalty;
        let isRefundable = false;
        let cancellationDetails = 'Non-refundable';

        if (cancelPenalties && cancelPenalties.length > 0) {
          const penalty = cancelPenalties[0];
          isRefundable = penalty.Refundable === true;
          
          if (penalty.Deadline && isRefundable) {
            const days = penalty.OffsetUnitMultiplier || 0;
            cancellationDetails = days > 0 
              ? `Free cancellation up to ${days} day${days > 1 ? 's' : ''} before arrival`
              : 'Free cancellation until check-in';
          } else if (isRefundable) {
            cancellationDetails = 'Free cancellation';
          }
        }

        return {
          ...plan,
          rateSource: plan.RateSource || plan.rateSource || SABRE_GDS_RATE_SOURCE,
          pricing: {
            total: totalAmount,
            base: baseAmount,
            perNight: pricePerNight,
            basePerNight: basePerNight,
            currency: currency
          },
          refundable: isRefundable,
          cancellationDetails: cancellationDetails,
          ratePlanName: plan.RatePlanName || 'Standard Rate',
          prepaid: convertedRateInfo.PrepaidIndicator === true,
          taxInclusive: convertedRateInfo.TaxInclusive === true,
          additionalDetails: (() => {
            const raw = convertedRateInfo.AdditionalDetails?.AdditionalDetail;
            if (!raw) return [];
            const arr = Array.isArray(raw) ? raw : [raw];
            const lines = arr.flatMap((d) => {
              if (!d) return [];
              // Sabre shape: { Code, Description, Text: [..] }
              if (Array.isArray(d.Text) && d.Text.length) {
                return d.Text.map((t) => String(t).trim()).filter(Boolean);
              }
              // Fallbacks
              const t = d.text ?? d.value ?? d.Description ?? d.description ?? null;
              return t ? [String(t).trim()] : [];
            });
            // De-dupe while preserving order
            const seen = new Set();
            return lines.filter((l) => {
              const k = String(l);
              if (seen.has(k)) return false;
              seen.add(k);
              return true;
            });
          })()
        };
      });

      if (enhancedRatePlans.length === 0) {
        return;
      }

      const processedRoom = {
        ...room,
        roomTypeName,
        bedInfo,
        ratePlans: enhancedRatePlans,
        amenities: (room.Amenities?.Amenity || []).map(amenity => ({
          description: amenity.Description || amenity,
          complimentary: amenity.ComplimentaryInd === true
        })),
        roomSize: (() => {
          const description = room.RoomDescription?.Text?.[0] || '';
          const sizeMatch = description.match(/(\d+SQFT|\d+SQM)/);
          return sizeMatch ? sizeMatch[0] : null;
        })(),
        roomView: room.RoomViewDescription || 'Standard View',
        roomDescription: room.RoomDescription?.Text?.[0] || null,
        maxOccupancy: {
          adults: room.Adults || 2,
          children: room.Children || 0
        },
        roomIndex: index
      };

      if (!groupedRooms[roomTypeName]) {
        groupedRooms[roomTypeName] = {
          rooms: [],
          minPrice: Infinity,
          maxOccupancy: 0,
          configurations: {}
        };
      }

      groupedRooms[roomTypeName].rooms.push(processedRoom);
      
      // Track min price and max occupancy for the room type
      enhancedRatePlans.forEach(plan => {
        if (plan.pricing.basePerNight < groupedRooms[roomTypeName].minPrice) {
          groupedRooms[roomTypeName].minPrice = plan.pricing.basePerNight;
        }
      });
      
      if (processedRoom.maxOccupancy.adults > groupedRooms[roomTypeName].maxOccupancy) {
        groupedRooms[roomTypeName].maxOccupancy = processedRoom.maxOccupancy.adults;
      }

      // Group by configuration (view + size + bed)
      const configKey = `${processedRoom.roomView}_${processedRoom.roomSize || 'standard'}_${processedRoom.bedInfo}`;
      if (!groupedRooms[roomTypeName].configurations[configKey]) {
        groupedRooms[roomTypeName].configurations[configKey] = {
          roomView: processedRoom.roomView,
          roomSize: processedRoom.roomSize,
          bedInfo: processedRoom.bedInfo,
          rooms: [],
          amenities: new Set(),
          minPrice: Infinity
        };
      }

      const config = groupedRooms[roomTypeName].configurations[configKey];
      config.rooms.push(processedRoom);
      
      // Collect amenities
      processedRoom.amenities.forEach(amenity => {
        config.amenities.add(amenity.description);
      });

      // Track min price for this configuration
      enhancedRatePlans.forEach(plan => {
        if (plan.pricing.basePerNight < config.minPrice) {
          config.minPrice = plan.pricing.basePerNight;
        }
      });
    });

    // Convert amenities sets to arrays
    Object.values(groupedRooms).forEach(roomType => {
      Object.values(roomType.configurations).forEach(config => {
        config.amenities = Array.from(config.amenities);
      });
    });

    return groupedRooms;
  }, [roomsData, loading, searchCriteria, isOffMarketStay]);

  // Calculate emissions for all rate plans when rooms data is loaded
  useEffect(() => {
    if (roomsByType && Object.keys(roomsByType).length > 0 && !loading) {
      setPreloadingEmissions(true);
      preloadAllEmissionsData().finally(() => {
        setPreloadingEmissions(false);
      });
    }
  }, [roomsByType, loading]);

  // Function to preload emissions data for all rate plans
  const preloadAllEmissionsData = async () => {
    
    const allRatePlans = [];
    
    // Collect all rate plans from all rooms
    Object.values(roomsByType).forEach(roomTypeData => {
      Object.values(roomTypeData.configurations).forEach(config => {
        config.rooms.forEach(room => {
          room.ratePlans.forEach(ratePlan => {
            allRatePlans.push({ room, ratePlan });
          });
        });
      });
    });

    // Calculate emissions for all rate plans in parallel with limited concurrency
    const batchSize = 5; // Process 5 at a time to avoid overwhelming the API
    for (let i = 0; i < allRatePlans.length; i += batchSize) {
      const batch = allRatePlans.slice(i, i + batchSize);
      await Promise.allSettled(
        batch.map(({ room, ratePlan }) => 
          calculateRatePlanEmissions(room, ratePlan, 'preload')
        )
      );
      
      // Small delay between batches to be respectful to the API
      if (i + batchSize < allRatePlans.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
  };

  const calculateRatePlanEmissions = async (room, ratePlan, source = 'manual') => {
    const emissionKey = `${room.roomIndex}-${ratePlan.RateKey}`;
    
    // Skip if already loading or loaded
    if (ratePlanEmissionsLoading[emissionKey] || ratePlanEmissions[emissionKey]) {
      return;
    }
    
    // Set loading state
    setRatePlanEmissionsLoading(prev => ({
      ...prev,
      [emissionKey]: true
    }));

    try {
      // Add fallback values for hotelData
      const emissionsPayload = {
        hotelLocation: {
          country: hotelData?.country || hotelData?.address?.country || 'Singapore',
          countryCode: hotelData?.countryCode || hotelData?.address?.countryCode || 'SG',
          state: hotelData?.city || hotelData?.address?.city || 'Singapore',
          city: hotelData?.city || hotelData?.address?.city || 'Singapore'
        },
        amenitiesList: getAmenitiesFromRoom(room),
        ecoCertified: false,
        occupancy: searchCriteria.adults || 1,
        roomType: room.roomTypeName?.toUpperCase().replace(/\s+/g, '_') || 'STANDARD',
        selectedRoomType: {
          sabreRoomType: room.RoomType || 'DELUXE_ROOM',
          roomTypeCode: room.RoomTypeCode || 77
        },
        numberOfNights: hotelService.calculateNights(searchCriteria.checkInDate, searchCriteria.checkOutDate),
        numberOfRooms: 1
      };
      
      const result = await hotelService.previewEmissions(emissionsPayload);      
      if (result.success) {
        setRatePlanEmissions(prev => ({
          ...prev,
          [emissionKey]: result.data
        }));
      } else {
        throw new Error(result.message || 'Failed to calculate emissions');
      }
    } catch (error) {
      console.error('❌ Failed to calculate emissions for rate plan:', error);
      setRatePlanEmissionsError(prev => ({
        ...prev,
        [emissionKey]: 'Unable to calculate impact'
      }));
    } finally {
      setRatePlanEmissionsLoading(prev => ({
        ...prev,
        [emissionKey]: false
      }));
    }
  };

  const renderEnvironmentalImpact = (room, ratePlan) => {
    const emissionKey = `${room.roomIndex}-${ratePlan.RateKey}`;
    const emissionsData = ratePlanEmissions[emissionKey];
    const isLoading = ratePlanEmissionsLoading[emissionKey];
    const hasError = ratePlanEmissionsError[emissionKey];

    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-1.5 px-2 rounded-md bg-emerald-50/60 border border-emerald-100/80">
          <div className="animate-spin rounded-full h-3 w-3 border-2 border-emerald-600 border-t-transparent mr-2"></div>
          <span className="text-xs text-slate-600">
            Calculating <span className="font-semibold text-emerald-800">environmental impact</span>…
          </span>
        </div>
      );
    }

    if (hasError) {
      return (
        <div className="text-center py-1">
          <div className="text-xs text-slate-500 flex items-center justify-center gap-1">
            <FaInfoCircle className="w-3 h-3 text-amber-600 shrink-0" />
            <span>
              <span className="font-semibold text-emerald-900">Environmental impact</span>
              <span className="text-slate-500"> unavailable for this rate</span>
            </span>
          </div>
        </div>
      );
    }

    if (emissionsData) {
      const carbonEmission = emissionsData.carbonEmission;
      const tierName = emissionsData.carbonTier?.tierName;
      
      return (
        <div
          className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between pt-1.5 pb-1.5 px-3 rounded-md bg-emerald-50/70 border border-emerald-200/80"
          role="status"
          aria-label="Environmental impact for this rate"
        >
            <div className="flex items-center gap-1 text-xs text-slate-700">
              <FaLeaf className="w-3.5 h-3.5 text-emerald-600 shrink-0" aria-hidden />
              <span className="font-semibold text-emerald-900 tracking-tight">Environmental impact</span>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="font-bold tabular-nums text-emerald-950">
                {carbonEmission >= 1000 
                  ? `${(carbonEmission / 1000).toFixed(1)} t CO₂`
                  : `${carbonEmission?.toFixed(0) || '0'} kg CO₂`
                }
              </span>
            </div>
            
            <div className={`text-xs px-2 py-0.5 rounded-full font-semibold self-start sm:self-auto ${
              tierName?.toLowerCase() === 'low impact' 
                ? 'bg-emerald-600 text-white'
                : tierName?.toLowerCase() === 'medium impact'
                ? 'bg-emerald-100 text-emerald-900 border border-emerald-200'
                : tierName?.toLowerCase() === 'high impact'
                ? 'bg-amber-100 text-amber-800 border border-amber-300'
                : 'bg-slate-100 text-slate-800 border border-slate-200'
            }`}>
              {tierName || 'Standard impact'}
            </div>
          </div>
      );
    }

    // If no data and not loading, show a fallback (this shouldn't happen with pre-loading)
    return (
      <div className="text-center py-1">
        <div className="text-xs text-slate-500">
          <span className="font-semibold text-emerald-800">Environmental impact</span>
          <span> loading…</span>
        </div>
      </div>
    );
  };

  // Helper function to extract amenities from room
  const getAmenitiesFromRoom = (room) => {
    const amenities = new Set();
    
    if (room.Amenities?.Amenity) {
      const roomAmenities = Array.isArray(room.Amenities.Amenity) 
        ? room.Amenities.Amenity 
        : [room.Amenities.Amenity];
      
      roomAmenities.forEach(amenity => {
        if (amenity.Code) {
          amenities.add(amenity.Code);
        }
      });
    }
    
    // Add common amenities
    const commonAmenities = [101, 102, 103, 104, 105, 106, 107, 108];
    commonAmenities.forEach(code => amenities.add(code));
    
    return Array.from(amenities);
  };

  // Get room type icon
  const getRoomTypeIcon = (roomType) => {
    const type = roomType.toLowerCase();
    if (type.includes('suite') || type.includes('presidential')) return <FaCrown className="text-slate-700" />;
    if (type.includes('deluxe') || type.includes('premium')) return <FaStar className="text-slate-600" />;
    if (type.includes('ocean') || type.includes('beach')) return <FaUmbrellaBeach className="text-slate-600" />;
    if (type.includes('city')) return <FaCity className="text-slate-600" />;
    if (type.includes('mountain')) return <FaMountain className="text-slate-600" />;
    if (type.includes('garden')) return <FaLeaf className="text-slate-600" />;
    return <FaBed className="text-slate-500" />;
  };

  // Get view icon
  const getViewIcon = (view) => {
    const viewType = view.toLowerCase();
    if (viewType.includes('ocean') || viewType.includes('sea')) return <FaUmbrellaBeach className="text-slate-600" />;
    if (viewType.includes('city')) return <FaCity className="text-slate-600" />;
    if (viewType.includes('mountain')) return <FaMountain className="text-slate-600" />;
    if (viewType.includes('garden') || viewType.includes('park')) return <FaLeaf className="text-slate-600" />;
    if (viewType.includes('pool')) return <FaSwimmingPool className="text-slate-600" />;
    return <FaEye className="text-slate-500" />;
  };

  // Get amenity icon
  const getAmenityIcon = (amenity) => {
    const desc = amenity.toLowerCase();
    if (desc.includes('wifi')) return <FaWifi className="text-slate-600" />;
    if (desc.includes('tv')) return <FaTv className="text-slate-600" />;
    if (desc.includes('coffee')) return <FaCoffee className="text-slate-600" />;
    if (desc.includes('shower') || desc.includes('bath')) return <FaShower className="text-slate-600" />;
    if (desc.includes('gym') || desc.includes('fitness')) return <FaDumbbell className="text-slate-600" />;
    if (desc.includes('parking')) return <FaParking className="text-slate-600" />;
    if (desc.includes('concierge') || desc.includes('service')) return <FaConciergeBell className="text-slate-600" />;
    return <FaCheck className="text-slate-600" />;
  };

  // Toggle room type expansion
  const toggleRoomType = (roomType) => {
    setExpandedRoomTypes(prev => ({
      ...prev,
      [roomType]: !prev[roomType]
    }));
  };

  // Toggle configuration expansion
  const toggleConfiguration = (roomType, configKey) => {
    const fullKey = `${roomType}_${configKey}`;
    setExpandedConfigurations(prev => ({
      ...prev,
      [fullKey]: !prev[fullKey]
    }));
  };

  // Collapse all dropdowns
  const collapseAll = () => {
    setExpandedRoomTypes({});
    setExpandedConfigurations({});
  };

  // Expand all dropdowns
  const expandAll = () => {
    const allRoomTypes = {};
    const allConfigurations = {};
    
    Object.keys(roomsByType).forEach(roomType => {
      allRoomTypes[roomType] = true;
      
      Object.keys(roomsByType[roomType].configurations).forEach(configKey => {
        allConfigurations[`${roomType}_${configKey}`] = true;
      });
    });
    
    setExpandedRoomTypes(allRoomTypes);
    setExpandedConfigurations(allConfigurations);
  };

  // Auto-expand all dropdowns when data loads
  React.useEffect(() => {
    if (roomsByType && Object.keys(roomsByType).length > 0) {
      // Auto-expand all room types
      const allRoomTypes = {};
      const allConfigurations = {};
      
      Object.keys(roomsByType).forEach(roomType => {
        allRoomTypes[roomType] = true;
        
        // Auto-expand all configurations for each room type
        Object.keys(roomsByType[roomType].configurations).forEach(configKey => {
          allConfigurations[`${roomType}_${configKey}`] = true;
        });
      });
      
      setExpandedRoomTypes(allRoomTypes);
      setExpandedConfigurations(allConfigurations);
    }
  }, [roomsByType]);

  // Handle room selection (multiple selection)
  const handleRoomSelection = (room, ratePlan) => {
    const newSelection = {
      id: `${room.roomIndex}-${ratePlan.RateKey}`,
      room,
      ratePlan,
      searchCriteria
    };
    
    // Check if already selected
    const existingIndex = selectedRooms.findIndex(selected => selected.id === newSelection.id);
    
    if (existingIndex >= 0) {
      // Remove if already selected
      const updatedRooms = selectedRooms.filter(selected => selected.id !== newSelection.id);
      setSelectedRooms(updatedRooms);
      toast.success(`Removed ${room.roomTypeName} - ${ratePlan.ratePlanName}`);
    } else {
      // Add new selection
      const updatedRooms = [...selectedRooms, newSelection];
      setSelectedRooms(updatedRooms);
      toast.success(`Added ${room.roomTypeName} - ${ratePlan.ratePlanName}`);
    }
    
    if (onRoomSelected) {
      onRoomSelected(selectedRooms);
    }
  };

  // Remove specific room selection
  const removeRoomSelection = (selectionId) => {
    const updatedRooms = selectedRooms.filter(selected => selected.id !== selectionId);
    setSelectedRooms(updatedRooms);
    const removedRoom = selectedRooms.find(selected => selected.id === selectionId);
    if (removedRoom) {
      toast.success(`Removed ${removedRoom.room.roomTypeName} - ${removedRoom.ratePlan.ratePlanName}`);
    }
  };

  // Clear all selections
  const clearAllSelections = () => {
    setSelectedRooms([]);
    toast.success('Cleared all room selections');
  };

   const processRatePlan = (ratePlan) => {
    return {
      ...ratePlan,
      RateKey: ratePlan.RateKey,
      ratePlanName: ratePlan.ratePlanName || 'Standard Rate',
      pricing: ratePlan.pricing || {
        total: 0,
        base: 0,
        perNight: 0,
        currency: searchCriteria.currency || 'USD'
      }
    };
  };

    // Handle price check for all selected rooms
  const handlePriceCheckForAll = async () => {
    if (selectedRooms.length === 0) {
      toast.error('Please select at least one room first');
      return;
    }

    setReserveLoading(true);
    setPriceCheckLoading(true);

    try {
      const priceCheckPromises = selectedRooms.map(async (selection) => {
        const priceCheckData = {
          rateKey: selection.ratePlan.RateKey,
          checkInDate: searchCriteria.checkInDate,
          checkOutDate: searchCriteria.checkOutDate
        };

        const priceCheckResult = await hotelService.priceCheckForBooking(priceCheckData);
        return {
          selection,
          result: priceCheckResult
        };
      });

      const priceCheckResults = await Promise.all(priceCheckPromises);
      
      // Check if all price checks were successful
      const allSuccessful = priceCheckResults.every(result => result.result.success);
      
      if (allSuccessful) {
        setPriceCheckResponses(priceCheckResults.map(result => result.result.data));
        setPendingSelections(selectedRooms);
        setConfirmationOpen(true);
        toast.success('Prices confirmed! Ready to review.');
      } else {
        toast.warning('Some prices may have changed. Please review before booking.');
        // Still show the confirmation but with a warning
        setPriceCheckResponses(priceCheckResults.map(result => result.result.data || {}));
        setPendingSelections(selectedRooms);
        setConfirmationOpen(true);
      }
    } catch (error) {
      console.error('Price check error:', error);
      toast.error('Unable to verify current pricing for some rooms');
    } finally {
      setPriceCheckLoading(false);
      setReserveLoading(false);
    }
  };

  const updateEmissionsData = (newEmissionsData) => {
    setEmissionsData(newEmissionsData);
  };

// Update the handleAddAllToCart function
const handleAddAllToCart = async () => {
  if (!pendingSelections.length || !priceCheckResponses.length) {
    toast.error('No rooms selected or price check data missing');
    return;
  }
  
  setAddToCartLoading(true);
  
  try {
    const cartResults = [];
    const round2Amt = (n) => Math.round(Number(n) * 100) / 100;
    
    for (let i = 0; i < pendingSelections.length; i++) {
      const selection = pendingSelections[i];
      const priceCheckResponse = priceCheckResponses[i];
      const selectionId = selection.id || i;

      /** Off-market cart context (resolved before commission / rate-source logic). */
      const offMarketPropertyIdResolved =
        (offMarketProperty?._id != null && String(offMarketProperty._id)) ||
        (offMarketProperty?.id != null && String(offMarketProperty.id)) ||
        (searchCriteria?.offMarketPropertyId != null && String(searchCriteria.offMarketPropertyId)) ||
        null;
      const isOffMarketStayCart =
        Boolean(offMarketProperty) ||
        (Boolean(searchCriteria?.offMarketStay) && Boolean(offMarketPropertyIdResolved));
      
      // Get emissions data for this specific room
      const emissionsDataForRoom = emissionsData[selectionId];
      
      // Validate price check response structure
      if (!priceCheckResponse?.HotelPriceCheckRS?.PriceCheckInfo) {
        console.error(`Invalid price check response for room ${i + 1}:`, priceCheckResponse);
        cartResults.push({
          success: false,
          room: selection.room.roomTypeName,
          message: 'Invalid price check data'
        });
        continue;
      }

      const priceCheckInfo = priceCheckResponse.HotelPriceCheckRS.PriceCheckInfo;
      const hotelInfo = priceCheckInfo.HotelInfo;
      const hotelRateInfo = priceCheckInfo.HotelRateInfo;
      
      // Get rate information - handle both array and single object cases
      const rateInfos = hotelRateInfo?.RateInfos;
      const convertedRateInfoPrimary = Array.isArray(rateInfos?.ConvertedRateInfo)
        ? rateInfos.ConvertedRateInfo[0]
        : rateInfos?.ConvertedRateInfo;
      
      // Get room information
      const roomInfo = hotelRateInfo?.Rooms?.Room?.[0];
      const ratePlanRaw = roomInfo?.RatePlans?.RatePlan;
      const ratePlan = Array.isArray(ratePlanRaw) ? ratePlanRaw[0] : ratePlanRaw;
      const ratePlanConvertedInfo = ratePlan?.ConvertedRateInfo;
      const convertedRateInfo = convertedRateInfoPrimary || ratePlanConvertedInfo;
      
      if (!convertedRateInfo) {
        console.error(`Missing converted rate info for room ${i + 1}`);
        cartResults.push({
          success: false,
          room: selection.room.roomTypeName,
          message: 'Missing rate information'
        });
        continue;
      }

      // Get image URL as a string
      const getHotelImageUrl = () => {
        if (hotelData?.images && Array.isArray(hotelData.images) && hotelData.images.length > 0) {
          const firstImage = hotelData.images[0];
          // If it's already a string, return it
          if (typeof firstImage === 'string') {
            return firstImage;
          }
          // If it's an object, extract the URL
          if (firstImage && typeof firstImage === 'object') {
            return firstImage.urls?.large || firstImage.urls?.medium || firstImage.urls?.small || firstImage.Url || firstImage.url ||
                   '/images/default-room.jpg';
          }
        }
        return '/images/default-room.jpg';
      };

      // Calculate nights
      const nights = hotelService.calculateNights(searchCriteria.checkInDate, searchCriteria.checkOutDate);
      
      // Parse pricing information
      const baseRate = parseFloat(convertedRateInfo.AmountBeforeTax || 0);
      const totalRate = parseFloat(convertedRateInfo.AmountAfterTax || 0);
      const taxAmount = totalRate - baseRate;
      
      // Process taxes from the rate info
      const taxes = [];
      if (hotelRateInfo.RateInfos?.RateInfo?.[0]?.Taxes?.TaxGroups?.TaxGroup) {
        const taxGroups = Array.isArray(hotelRateInfo.RateInfos.RateInfo[0].Taxes.TaxGroups.TaxGroup)
          ? hotelRateInfo.RateInfos.RateInfo[0].Taxes.TaxGroups.TaxGroup
          : [hotelRateInfo.RateInfos.RateInfo[0].Taxes.TaxGroups.TaxGroup];
        
        taxGroups.forEach(tax => {
          taxes.push({
            name: tax.Description || 'Tax',
            amount: parseFloat(tax.Amount || 0),
            currency: tax.CurrencyCode || convertedRateInfo.CurrencyCode
          });
        });
      }

      // Process daily rates
      const dailyRates = [];
      if (ratePlanConvertedInfo?.Rates?.Rate) {
        const rates = Array.isArray(ratePlanConvertedInfo.Rates.Rate)
          ? ratePlanConvertedInfo.Rates.Rate
          : [ratePlanConvertedInfo.Rates.Rate];
        
        rates.forEach(rate => {
          dailyRates.push({
            date: rate.StartDate,
            amount: parseFloat(rate.AmountBeforeTax || 0),
            currency: rate.CurrencyCode || convertedRateInfo.CurrencyCode
          });
        });
      }

      const cancelPenaltyRaw = ratePlanConvertedInfo?.CancelPenalties?.CancelPenalty;
      const cancelPenalty = Array.isArray(cancelPenaltyRaw) ? cancelPenaltyRaw[0] : cancelPenaltyRaw;
      const isRefundable = cancelPenalty?.Refundable === true;
      const absoluteDeadlineRaw = cancelPenalty?.Deadline?.AbsoluteDeadline;
      let cancellationDeadline = absoluteDeadlineRaw ? new Date(absoluteDeadlineRaw) : null;
      if (cancellationDeadline && Number.isNaN(cancellationDeadline.getTime())) {
        cancellationDeadline = null;
      }
      // Support offset-based deadline shape from Sabre when AbsoluteDeadline is missing.
      if (!cancellationDeadline && cancelPenalty?.Deadline && searchCriteria?.checkInDate) {
        const offsetUnit = String(cancelPenalty.Deadline.OffsetTimeUnit || '').toLowerCase();
        const offsetValue = Number(cancelPenalty.Deadline.OffsetUnitMultiplier || 0);
        if (offsetUnit.includes('day') && Number.isFinite(offsetValue)) {
          const checkInDate = new Date(searchCriteria.checkInDate);
          if (!Number.isNaN(checkInDate.getTime())) {
            checkInDate.setDate(checkInDate.getDate() - offsetValue);
            cancellationDeadline = checkInDate;
          }
        }
      }

      // Apply markup first, then compute Stripe processing fees on the final payable amount.
      const ratePlanForCommission = selection?.ratePlan || ratePlan;
      const commissionFloor = getRateCommissionFloor(ratePlanForCommission);
      const { rateSource, rateSourceLabel } = isOffMarketStayCart
        ? resolveRateSourceFromPlan({ rateSource: SABRE_GDS_RATE_SOURCE })
        : resolveRateSourceFromPlan({
            ...ratePlanForCommission,
            rateSource:
              ratePlan?.RateSource ??
              ratePlan?.rateSource ??
              ratePlanForCommission?.rateSource ??
              ratePlanForCommission?.RateSource
          });
      const markupPct = getEffectiveMarkup(selection.id, ratePlanForCommission);
      const commissionDefaultPct = isOffMarketStayCart
        ? OFF_MARKET_DEFAULT_COMMISSION_PCT
        : commissionFloor;
      const markupBreakdown = getAgentMarkupBreakdown(totalRate, markupPct, true, {
        defaultCommissionPct: commissionDefaultPct
      });
      const payableBeforeFees = getYouPay(totalRate, markupPct, commissionFloor);

      // Format the data according to cart service expectations
      const hotelProcessingResult = await calculateHotelProcessingFees(payableBeforeFees, convertedRateInfo.CurrencyCode);
      const hotelProcessing = {
        fees: hotelProcessingResult?.fees || {},
        totalProcessingFees: Number(hotelProcessingResult?.totalProcessingFees || 0),
        totalWithFees: Number(hotelProcessingResult?.totalWithFees || payableBeforeFees),
        currency: hotelProcessingResult?.currency || convertedRateInfo.CurrencyCode
      };
      const totalWithProcessingFees = hotelProcessing.totalWithFees;
      const totalBeforeProcessingFees = payableBeforeFees;

      const inclusionsText = (() => {
        const raw = selection?.ratePlan?.additionalDetails;
        if (!raw) return 'Standard amenities';
        const arr = Array.isArray(raw) ? raw : [raw];
        const parts = arr
          .map((v) => {
            if (v == null) return null;
            if (typeof v === 'string') return v.trim();
            if (typeof v === 'number') return String(v);
            if (typeof v === 'object') {
              const t =
                v.text ??
                v.value ??
                v.label ??
                v.name ??
                v.description ??
                v.Description ??
                null;
              return t ? String(t).trim() : null;
            }
            return String(v).trim();
          })
          .filter(Boolean);
        return parts.length ? parts.join(', ') : 'Standard amenities';
      })();

      const cartData = {
        type: 'hotel',
        title: hotelInfo.HotelName,
        image: getHotelImageUrl(),
        price: totalWithProcessingFees,
        totalWithProcessingFees,
        totalBeforeProcessingFees,
        totalTaxesAndFees: taxAmount + hotelProcessing.totalProcessingFees,
        processingFees: hotelProcessing,
        currency: convertedRateInfo.CurrencyCode,
        selectedDates: `${searchCriteria.checkInDate} - ${searchCriteria.checkOutDate}`,
        checkInDate: searchCriteria.checkInDate,
        checkOutDate: searchCriteria.checkOutDate,
        nights: nights,
        maxAdults: selection.room.maxOccupancy.adults,
        maxChildren: selection.room.maxOccupancy.children,
        bedType: { type: selection.room.bedInfo },
        inclusions: inclusionsText,
        cancellationPolicy: ratePlanConvertedInfo?.CancelPenalties || selection?.ratePlan?.cancellationDetails,
        cancellationDetails: selection?.ratePlan?.cancellationDetails || 'Cancellation policy not available',
        refundable: isRefundable,
        isRefundable: isRefundable,
        cancellationDeadline: cancellationDeadline ? cancellationDeadline.toISOString() : null,
        depositPolicy: 'No deposit required',
        taxes: taxes,
        fees: [],
        priceDetails: {
          baseRate: baseRate,
          tax: taxAmount,
          additionalFees: hotelProcessing.totalProcessingFees,
          totalWithFees: totalWithProcessingFees
        },
        hotelId: hotelCode,
        roomTypeCode: selection.room.RoomTypeCode || roomInfo?.RoomTypeCode || 'N/A',
        ratePlanName: selection?.ratePlan?.ratePlanName || ratePlan?.RatePlanName || 'Standard Rate',
        ratePlanCode: selection?.ratePlan?.RatePlanType || ratePlan?.RatePlanCode || 'N/A',
        rateKey: selection?.ratePlan?.RateKey || ratePlan?.RateKey || null,
        rateSource,
        rateSourceLabel,
        searchParams: {
          checkIn: searchCriteria.checkInDate,
          checkOut: searchCriteria.checkOutDate,
          adults: searchCriteria.adults,
          children: searchCriteria.children,
          currency: searchCriteria.currency
        },
        hotelDetails: {
          name: hotelInfo.HotelName,
          rating: hotelInfo.SabreRating,
          address: hotelInfo.LocationInfo?.Address,
          contact: hotelInfo.LocationInfo?.Contact,
          chainName: hotelInfo.ChainName
        },
        rateDetails: {
          dailyRates: dailyRates,
          dailyRatesTotal: baseRate,
          convertedRateInfo: convertedRateInfo,
          cancellationPolicy: ratePlanConvertedInfo?.CancelPenalties
        },
        // ADD SUSTAINABILITY DATA HERE
        sustainabilityData: emissionsDataForRoom ? {
          carbonEmission: emissionsDataForRoom.carbonEmission,
          carbonTier: emissionsDataForRoom.carbonTier,
          breakdown: emissionsDataForRoom.breakdown,
          calculationMethod: emissionsDataForRoom.calculationMethod,
          offsetRecommendation: emissionsDataForRoom.offsetRecommendation
        } : null,
        /** Whether this cart item came from the off-market stays catalogue. */
        isOffMarketStay: isOffMarketStayCart,
        offMarketPropertyId: offMarketPropertyIdResolved,
        /**
         * Snapshot of the off-market hotel's special commission at add-to-cart time.
         * Stored so checkout / reporting can reference original breakdown.
         */
        offMarketCommission: offMarketCommission
          ? {
              enabled: true,
              totalPct: offMarketCommission.total,
              defaultBasePct: offMarketCommission.defaultBase,
              specialAddedPct: offMarketCommission.specialAdded,
            }
          : isOffMarketStayCart
            ? {
                enabled: false,
                totalPct: OFF_MARKET_DEFAULT_COMMISSION_PCT,
                defaultBasePct: OFF_MARKET_DEFAULT_COMMISSION_PCT,
                specialAddedPct: 0,
              }
            : null,
        agentCommission: {
          ...buildAgentCommissionSnapshot({
            // Base for commission math should stay the pre-markup stay total (after tax).
            baseBookingTotal: totalRate,
            markupPercentage: markupPct,
            markupEnabled: true,
            defaultCommissionPct: isOffMarketStayCart
              ? OFF_MARKET_DEFAULT_COMMISSION_PCT
              : commissionFloor,
            payableIncludesMarkup: true,
            payableTotalBeforeProcessingFees: payableBeforeFees,
            payableTotalWithProcessingFees: totalWithProcessingFees,
            selectionId: selection.id
          }),
          markupPercentage: markupPct,
          defaultCommissionPct: isOffMarketStayCart
            ? OFF_MARKET_DEFAULT_COMMISSION_PCT
            : commissionFloor,
          defaultCommissionAmount: round2Amt(
            (totalRate *
              (isOffMarketStayCart ? OFF_MARKET_DEFAULT_COMMISSION_PCT : commissionFloor)) /
              100
          ),
          totalCommissionAmount: round2Amt((totalRate * markupPct) / 100),
          /** $ added to customer price — only above markupBaselinePct. */
          markupAmount: getIncrementalAmount(totalRate, markupPct, commissionFloor),
          totalAgentPays: payableBeforeFees,
          /** Floor for "added to price" (7% BDC, 10% GDS, or off-market special). */
          markupBaselinePct: commissionFloor,
          /** Agent uplift above commission floor (percentage points). */
          offMarketStayMarkup: getIncrementalPct(markupPct, commissionFloor),
        }
      };

      try {
        const result = await cartService.addToCart(cartData);
        cartResults.push({
          success: result.success,
          room: selection.room.roomTypeName,
          message: result.message || 'Added successfully'
        });
      } catch (error) {
        console.error(`Error adding room ${i + 1} to cart:`, error);
        cartResults.push({
          success: false,
          room: selection.room.roomTypeName,
          message: error.message || 'Failed to add to cart'
        });
      }
    }

    // Check results and provide feedback
    const successfulAdds = cartResults.filter(result => result.success);
    const failedAdds = cartResults.filter(result => !result.success);

    if (failedAdds.length === 0) {
      if (successfulAdds.length > 0) {
        refreshCartCount();
      }
      toast.success(`All ${successfulAdds.length} rooms added to cart successfully!`);
      setConfirmationOpen(false);
      setSelectedRooms([]);
      setPriceCheckResponses(null);
      setPendingSelections([]);
      
      if (onRoomSelected) {
        onRoomSelected([]);
      }
    } else if (successfulAdds.length > 0) {
      refreshCartCount();
      toast.warning(`${successfulAdds.length} rooms added to cart. ${failedAdds.length} failed.`);
    } else {
      toast.error('Failed to add any rooms to cart. Please try again.');
    }
  } catch (error) {
    console.error('Error in handleAddAllToCart:', error);
    toast.error('Error adding rooms to cart. Please try again.');
  } finally {
    setAddToCartLoading(false);
  }
};

  // Calculate total price for all selected rooms
  const calculateTotalPrice = () => {
    return selectedRooms.reduce((total, selection) => {
      return total + (selection.ratePlan.pricing?.total || 0);
    }, 0);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <LoadingSpinner message="Loading available rooms..." />
      </div>
    );
  }

  if (Object.keys(roomsByType).length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <FaBed className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-light text-gray-900 mb-3 typography-subsection-title">No Rooms Available</h3>
          <p className="text-gray-600 mb-6 font-poppinsLight">
            No rooms are available for your selected dates. Try adjusting your search criteria.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-4${selectedRooms.length > 0 ? ' pb-24 sm:pb-20' : ''}`}>
      {/* Global Loading Indicator for Emissions */}
      {/* {preloadingEmissions && (
        <div className="fixed top-4 right-4 bg-slate-700 text-white px-4 py-2 rounded-lg shadow-lg z-50">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            <span>Calculating environmental impact...</span>
          </div>
        </div>
      )} */}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 space-y-4 lg:space-y-0">
          <div className="flex-1">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-light text-slate-800 mb-2 typography-section-title leading-tight tracking-wide">Available Rooms & Suites</h2>
            <p className="text-base md:text-lg text-slate-600 font-poppinsLight font-light">
              Explore room types, views, and rate options. All sections are automatically expanded for easy browsing.
            </p>
            {preloadingEmissions && (
              <div className="mt-2 text-sm text-slate-600 flex items-center font-poppinsLight rounded-md bg-emerald-50/70 border border-emerald-100 px-3 py-2">
                <div className="animate-spin rounded-full h-3 w-3 border-2 border-emerald-600 border-t-transparent mr-2 shrink-0"></div>
                <span>
                  Loading <span className="font-semibold text-emerald-900">environmental impact</span> data for rates…
                </span>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-3 w-full lg:w-auto lg:flex-row lg:items-center lg:justify-end lg:shrink-0 lg:gap-4">
            {selectedRooms.length > 0 && (
              <button
                type="button"
                onClick={() => setSelectionDrawerOpen(true)}
                className="text-left sm:text-right sm:mr-4 rounded-lg sm:rounded-none px-2 py-1 -mx-2 sm:mx-0 sm:px-0 sm:py-0 hover:bg-gray-100/80 sm:hover:bg-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
              >
                <div className="text-sm md:text-base text-gray-600">
                  <span className="sm:hidden">Selected — tap to open cart</span>
                  <span className="hidden sm:inline">Selected — click to open cart</span>
                </div>
                <div className="font-bold text-lg text-gray-900">{selectedRooms.length} room{selectedRooms.length > 1 ? 's' : ''}</div>
              </button>
            )}
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                onClick={expandAll}
                className="text-sm md:text-base bg-gray-100 text-gray-700 px-3 md:px-4 py-2 rounded hover:bg-gray-200 transition-colors w-full sm:w-auto"
              >
                Expand All
              </button>
              <button
                onClick={collapseAll}
                className="text-sm md:text-base bg-gray-100 text-gray-700 px-3 md:px-4 py-2 rounded hover:bg-gray-200 transition-colors w-full sm:w-auto"
              >
                Collapse All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Room Types Explorer - Horizontal Layout */}
      <div className="space-y-4">
        {Object.entries(roomsByType).map(([roomType, roomTypeData]) => {
          const isExpanded = expandedRoomTypes[roomType];
          const configurations = Object.entries(roomTypeData.configurations);
          
          return (
            <div key={roomType} className="bg-white rounded-lg shadow-sm border border-gray-300 overflow-hidden cursor-pointer hover:border-gray-400 hover:shadow-sm transition-all duration-150">
              {/* Main Container - Responsive Layout */}
              <div className="flex flex-col lg:flex-row">
                {/* Room Type Column - Always Visible */}
                <div className={`w-full lg:w-80 border-b lg:border-b-0 lg:border-r transition-all ${
                  isExpanded ? 'border-slate-300 bg-slate-50' : 'border-gray-200'
                }`}>
                  <button
                    onClick={() => toggleRoomType(roomType)}
                    className={`w-full p-4 md:p-6 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-inset ${
                      isExpanded ? 'bg-slate-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <div className="flex-shrink-0">
                          {getRoomTypeIcon(roomType)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-lg md:text-xl lg:text-2xl font-light text-slate-800 typography-card-title leading-tight tracking-wide break-words">{roomType}</h3>
                          <div className="space-y-1 mt-1 text-sm md:text-base text-slate-600">
                            <div className="flex items-center">
                              <FaUsers className="w-4 h-4 mr-2 flex-shrink-0" />
                              <span>Up to {roomTypeData.maxOccupancy} guests</span>
                            </div>
                            <div className="font-medium text-slate-800">
                              From {hotelService.formatCurrency(roomTypeData.minPrice, searchCriteria.currency)} per night
                            </div>
                            <div className="text-xs md:text-sm text-slate-500">
                              {configurations.length} configuration{configurations.length > 1 ? 's' : ''}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex-shrink-0 ml-2">
                        <FaChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${
                          isExpanded ? 'rotate-90' : ''
                        }`} />
                      </div>
                    </div>
                  </button>
                </div>

                {/* Available Room Views with Rates - Right Side (shown when collapsed) */}
                {!isExpanded && (
                  <div 
                    className="flex-1 bg-gray-50 p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => toggleRoomType(roomType)}
                  >
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-base md:text-lg font-light text-slate-800 typography-card-title tracking-wide">Available Room Views & Rates</h4>
                        <div className="flex items-center text-xs md:text-sm text-gray-500">
                          <span className="hidden sm:inline">Click to expand</span>
                          <span className="sm:hidden">Tap to expand</span>
                          <FaChevronRight className="w-3 h-3 ml-1" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 md:gap-3">
                        {configurations.map(([configKey, config]) => (
                          <div 
                            key={configKey} 
                            className="bg-white rounded-lg border border-gray-200 p-2 md:p-3 hover:border-slate-300 transition-colors"
                          >
                            {/* Room View Header */}
                            <div className="text-center mb-2 md:mb-3">
                              <div className="flex justify-center mb-1 md:mb-2">
                                {getViewIcon(config.roomView)}
                              </div>
                              <h5 className="font-light text-slate-800 text-xs md:text-sm mb-1 typography-body-sm tracking-wide truncate">{config.roomView}</h5>
                              <div className="text-xs text-gray-600 mb-1 md:mb-2 truncate">{config.bedInfo}</div>
                              <div className="text-xs md:text-sm font-medium text-slate-800">
                                From {hotelService.formatCurrency(config.minPrice, searchCriteria.currency)}
                              </div>
                              <div className="text-xs text-gray-500">per night</div>
                            </div>
                            
                            {/* Configuration Details */}
                            <div className="text-center space-y-1 md:space-y-2">
                              <div className="text-xs text-slate-500 font-medium">
                                {config.rooms.length} rate plan{config.rooms.length > 1 ? 's' : ''} available
                              </div>
                              <div className="text-xs text-slate-400 font-light">
                                View rates
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Configurations with Inline Rate Plans - Row Layout */}
                {isExpanded && (
                  <div className="flex-1 bg-gray-50">
                    <div className="p-4">
                      <h4 className="text-lg md:text-xl font-light text-slate-800 mb-4 typography-card-title tracking-wide">Room Configurations & Rate Plans</h4>
                      <div className="space-y-4 md:space-y-6">
                        {configurations.map(([configKey, config], configIndex) => {
                          const isConfigExpanded = expandedConfigurations[`${roomType}_${configKey}`];
                          
                          return (
                            <div 
                              key={configKey} 
                              className={`bg-white rounded-lg border-2 border-gray-400 shadow-sm overflow-hidden transition-all ${
                                isConfigExpanded 
                                  ? 'border-slate-400 shadow-md' 
                                  : 'cursor-pointer hover:border-gray-500 hover:shadow-md'
                              }`}
                              onClick={!isConfigExpanded ? () => toggleConfiguration(roomType, configKey) : undefined}
                            >
                              {/* Configuration Row */}
                              <div className="flex flex-col lg:flex-row">
                                {/* Configuration Details - Left Side */}
                                <div className="w-full lg:w-80 border-b lg:border-b-0 lg:border-r border-gray-200 bg-slate-50">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleConfiguration(roomType, configKey);
                                    }}
                                    className={`w-full p-3 md:p-4 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-inset ${
                                      isConfigExpanded ? 'bg-slate-50 hover:bg-gray-50' : 'hover:bg-gray-50'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center space-x-3 md:space-x-4 flex-1 min-w-0">
                                        <div className="flex-shrink-0">
                                          {getViewIcon(config.roomView)}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                           <h5 className="font-light text-slate-800 text-sm md:text-base typography-body tracking-wide truncate">{config.roomView}</h5>
                                          <div className="text-xs md:text-sm text-gray-600 mt-1">
                                            <div className="truncate">{config.bedInfo}</div>
                                            <div className="font-medium text-slate-800">
                                              From {hotelService.formatCurrency(config.minPrice, searchCriteria.currency)}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex-shrink-0 ml-2">
                                        <FaChevronRight className={`w-3 h-3 text-gray-400 transition-transform ${
                                          isConfigExpanded ? 'rotate-90' : ''
                                        }`} />
                                      </div>
                                    </div>
                                  </button>

                                  {/* Amenities Preview */}
                                  {config.amenities.length > 0 && (
                                    <div className="px-3 md:px-4 pb-3 md:pb-4">
                                      <div className={`rounded-lg p-2 md:p-3 border ${
                                        isConfigExpanded ? 'bg-slate-100 border-slate-200' : 'bg-gray-50 border-gray-200'
                                      }`}>
                                        <h6 className="text-xs font-light text-slate-500 mb-2 typography-body-sm tracking-wide uppercase">Room Amenities</h6>
                                        <div className="flex flex-wrap gap-1">
                                          {config.amenities.slice(0, 4).flatMap((amenity, idx) => {
                                            // Split amenities by common separators and flatten
                                            const splitAmenities = amenity.split(/[\/,;|]/).map(item => item.trim()).filter(item => item.length > 0);
                                            return splitAmenities.map((splitAmenity, splitIdx) => (
                                              <span key={`${idx}-${splitIdx}`} className="inline-flex items-center text-xs bg-white border border-slate-200 text-slate-600 px-1 md:px-2 py-1 rounded-md shadow-sm">
                                                {getAmenityIcon(splitAmenity)}
                                                <span className="ml-1 hidden sm:inline">{splitAmenity}</span>
                                              </span>
                                            ));
                                          })}
                                          {config.amenities.length > 4 && (
                                            <span className="text-xs text-slate-400 bg-white px-1 md:px-2 py-1 rounded-md border border-slate-200">
                                              +{config.amenities.length - 4} more
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Rate Plans - Right Side (shown when collapsed) */}
                                {!isConfigExpanded && (
                                  <div className="flex-1 bg-gray-50 p-3 md:p-4">
                                    <div>
                                      <h6 className="text-xs font-medium text-gray-600 mb-2">Available Rates</h6>
                                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                                        {config.rooms.flatMap(room => 
                                          room.ratePlans.map(ratePlan => ({ room, ratePlan }))
                                        ).slice(0, 5).map(({ room, ratePlan }, index) => {
                                          return (
                                            <div
                                              key={`${room.roomIndex}-${ratePlan.RateKey}-${index}`}
                                              className="p-2 bg-white rounded border border-gray-200"
                                            >
                                              <div className="text-center">
                                                <h6 className="font-medium text-gray-900 text-xs mb-1 truncate">
                                                  {ratePlan.ratePlanName}
                                                </h6>
                                                <div className="text-xs md:text-sm font-bold text-gray-900 mb-0.5">
                                                  {hotelService.formatCurrency(ratePlan.pricing.basePerNight, ratePlan.pricing.currency)}
                                                </div>
                                                <div className="text-[10px] text-gray-400 mb-1">/night</div>
                                                <div className="text-xs text-gray-500 mb-1">
                                                  {ratePlan.refundable ? 'Refundable' : 'Non-refundable'}
                                                </div>
                                                <div className="text-xs text-gray-400">
                                                  View rates
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Rate Plans - Right Side */}
                                {isConfigExpanded && (
                                  <div className="flex-1 bg-slate-50 p-3 md:p-4">
                                    <h6 className="text-sm md:text-base font-light text-slate-700 mb-3 typography-body tracking-wide">Available Rate Plans</h6>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                      {config.rooms.map((room) => 
                                        room.ratePlans.map((ratePlan, planIndex) => {
                                          const selectionId = `${room.roomIndex}-${ratePlan.RateKey}`;
                                          const isSelected = selectedRooms.some(selected => selected.id === selectionId);
                                          
                                          return (
                                            <div
                                              key={`${room.roomIndex}-${planIndex}`}
                                              className={`group bg-white border transition-all duration-200 cursor-pointer ${
                                                isSelected
                                                  ? 'border-2 border-slate-400 bg-slate-50 shadow-md'
                                                  : 'border border-slate-200 hover:border-slate-300 hover:shadow-sm'
                                              }`}
                                              onClick={() => handleRoomSelection(room, ratePlan)}
                                            >
                                              <div className="p-3 md:p-4">
                                                <div className="space-y-2 md:space-y-3">
                                                  {/* Rate Plan Name */}
                                                  <div className="text-center">
                                                    <h6 className="font-light text-slate-800 text-xs md:text-sm typography-body-sm tracking-wide uppercase">
                                                      {ratePlan.ratePlanName}
                                                    </h6>
                                                  </div>

                                                  {/* Rate Plan Details */}
                                                  <div className="text-center space-y-2">
                                                    {/* Rate Category Label */}
                                                    <div className="text-xs font-medium text-slate-600">
                                                      {ratePlan.rateCategory || (() => {
                                                        const price = ratePlan.pricing?.perNight || 0;
                                                        if (price < 400) return 'Best Value';
                                                        if (price < 450) return 'Standard Rate';
                                                        if (price < 500) return 'Flexible Rate';
                                                        if (price < 550) return 'Premium Rate';
                                                        return 'Deluxe Rate';
                                                      })()}
                                                    </div>
                                                    
                                                    {/* Cancellation Policy with Dates */}
                                                    {ratePlan.cancellationDetails && (
                                                      <div className="text-xs text-slate-500">
                                                        {ratePlan.cancellationDetails}
                                                      </div>
                                                    )}
                                                    
                                                    {/* Meal Plan */}
                                                    {ratePlan.mealPlan && ratePlan.mealPlan !== 'Room only' && (
                                                      <div className="text-xs text-slate-500">
                                                        {ratePlan.mealPlan}
                                                      </div>
                                                    )}
                                                    
                                                    {/* Rate Benefits */}
                                                    <div className="text-xs text-slate-500">
                                                      {ratePlan.rateBenefits || (() => {
                                                        const price = ratePlan.pricing?.perNight || 0;
                                                        if (price < 400) return 'Basic booking terms';
                                                        if (price < 450) return 'Standard booking terms';
                                                        if (price < 500) return 'Flexible booking terms';
                                                        if (price < 550) return 'Premium booking terms';
                                                        return 'Maximum flexibility';
                                                      })()}
                                                    </div>
                                                  </div>

                                                  {/* Pricing Section */}
                                                  <div className="text-center space-y-1">
                                                    <div className="text-xl md:text-2xl font-semibold text-slate-900 tabular-nums">
                                                      {hotelService.formatCurrency(ratePlan.pricing.basePerNight, ratePlan.pricing.currency)}
                                                    </div>
                                                    <div className="text-xs text-slate-500 font-light">
                                                      per night
                                                    </div>
                                                    <div className="text-xs md:text-sm font-light text-slate-600">
                                                      Subtotal: {hotelService.formatCurrency(ratePlan.pricing.base, ratePlan.pricing.currency)}
                                                    </div>
                                                   <div className="text-xs text-slate-500 font-light">
                                                      {ratePlan.ConvertedRateInfo.Guarantee.GuaranteeType === 'DEP' && 'Prepaid Guarantee'}
                                                      {ratePlan.ConvertedRateInfo.Guarantee.GuaranteeType === 'LATE' && 'Hotel Collect Guarantee'}
                                                      {ratePlan.ConvertedRateInfo.Guarantee.GuaranteeType === 'GUAR' && 'Hotel Collect Guarantee'}
                                                      {!['DEP', 'LATE', 'GUAR'].includes(ratePlan.ConvertedRateInfo.Guarantee.GuaranteeType) && 'Unidentified'}
                                                    </div>
                                                  </div>

                                                  {/* Features */}
                                                  <div className="space-y-1">
                                                    <div className="flex flex-wrap gap-1 md:gap-2 justify-center">
                                                      <div className={`inline-flex items-center px-1 md:px-2 py-1 text-xs font-semibold ${
                                                        ratePlan.refundable
                                                          ? 'bg-emerald-50 text-emerald-900 border border-emerald-200'
                                                          : 'bg-neutral-200 text-slate-800 border border-slate-300'
                                                      }`}>
                                                        {ratePlan.refundable ? (
                                                          <FaCheck className="w-3 h-3 mr-1" />
                                                        ) : (
                                                          <FaTimes className="w-3 h-3 mr-1" />
                                                        )}
                                                        <span className="hidden sm:inline">{ratePlan.refundable ? 'Refundable' : 'Non-refundable'}</span>
                                                        <span className="sm:hidden">{ratePlan.refundable ? 'Refund' : 'No Refund'}</span>
                                                      </div>
                                                    </div>

                                                    {renderEnvironmentalImpact(room, ratePlan)}
                                                    
                                                    {/* Limited Availability Warning */}
                                                    {ratePlan.limitedAvailability && (
                                                      <div className="text-center">
                                                        <div className="text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">
                                                          Limited availability
                                                        </div>
                                                      </div>
                                                    )}
                                                  </div>

                                                  {showMarkupBreakdown && (() => {
                                                    const total = ratePlan.pricing?.total ?? 0;
                                                    const currency = ratePlan.pricing?.currency || searchCriteria.currency || 'USD';
                                                    const effectivePct = getEffectiveMarkup(selectionId, ratePlan);
                                                    const rateFloor = getRateCommissionFloor(ratePlan);
                                                    const br = getAgentMarkupBreakdown(total, effectivePct, true, {
                                                      defaultCommissionPct: rateFloor
                                                    });
                                                    const hasOverride = roomMarkupOverrides[selectionId] !== undefined;
                                                    return (
                                                      <div className="relative group/commwrap mt-2">
                                                        {/* Tooltip */}
                                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 pointer-events-none opacity-0 group-hover/commwrap:opacity-100 transition-opacity duration-200">
                                                          <div className="bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl whitespace-nowrap">
                                                            <p className="font-semibold mb-0.5">Your commission at {effectivePct}%</p>
                                                            <p className="text-slate-400 text-[11px]">Click to adjust your markup rate</p>
                                                          </div>
                                                          <div className="w-2.5 h-2.5 bg-slate-900 rotate-45 mx-auto -mt-1.5 rounded-sm" />
                                                        </div>

                                        <button
                                          type="button"
                                          onClick={(e) => { e.stopPropagation(); openCommissionPanel(room, ratePlan, selectionId); }}
                                          className="w-full text-left rounded-xl overflow-hidden transition-all duration-150 group/comm"
                                          style={{ background: '#f1f5f9', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: '1px solid #cbd5e1', borderTop: `4px solid ${offMarketCommission ? '#d4af37' : '#1e293b'}` }}
                                        >
                                          <div className="px-3 pt-3 pb-3">
                                            <div className="flex items-center justify-between mb-2">
                                              <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Your commission</span>
                                              <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded border border-slate-300 bg-white text-slate-500">
                                                Advisors only
                                              </span>
                                            </div>
                                            {offMarketCommission && offMarketCommission.specialAdded > 0 && (
                                              <div className="mb-2 rounded-xl border border-yellow-400/45 bg-gradient-to-r from-amber-50/95 via-yellow-50 to-amber-50/95 px-2.5 py-2 shadow-[0_2px_12px_rgba(212,175,55,0.12)] ring-1 ring-yellow-200/55">
                                                <div className="flex items-start gap-2">
                                                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gradient-to-b from-yellow-400 to-amber-500 text-[11px] font-bold leading-none text-white shadow-sm">
                                                    +
                                                  </span>
                                                  <div className="min-w-0">
                                                    <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-slate-500">Special commission</p>
                                                    <p className="mt-1 text-[10px] font-medium leading-snug text-slate-800 tabular-nums">
                                                      <span className="text-slate-600">{offMarketCommission.defaultBase}%</span>
                                                      <span className="mx-0.5 text-slate-400">+</span>
                                                      <span className="font-semibold text-yellow-600">{offMarketCommission.specialAdded}%</span>
                                                      <span className="mx-1 text-slate-300">=</span>
                                                      <span className="font-bold text-slate-900">{offMarketCommission.total}% floor</span>
                                                    </p>
                                                  </div>
                                                </div>
                                              </div>
                                            )}
                                            <div className="flex items-baseline justify-between gap-2">
                                              <span className="text-sm text-slate-500">
                                                {effectivePct}% · est. earnings
                                              </span>
                                              <span className="text-2xl font-black text-slate-900 tabular-nums tracking-tight">
                                                {hotelService.formatCurrency(br.totalCommissionAmount, currency)}
                                              </span>
                                            </div>
                                          </div>
                                          <div className="flex items-center justify-between px-3 py-2.5 group-hover/comm:opacity-90 transition-opacity" style={{ background: offMarketCommission ? '#d4af37' : '#1e293b' }}>
                                            <span className={`text-xs font-semibold tracking-wide ${offMarketCommission ? 'text-neutral-900' : 'text-slate-200'}`}>
                                              {hasOverride ? 'Custom rate set — edit' : 'Adjust markup rate'}
                                            </span>
                                            <svg className={`w-3.5 h-3.5 ${offMarketCommission ? 'text-neutral-800' : 'text-slate-300'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                                          </div>
                                        </button>
                                                      </div>
                                                    );
                                                  })()}

                                                  {/* Action Button */}
                                                  <button
                                                    className={`w-full px-3 md:px-4 py-2 text-xs md:text-sm font-medium transition-all duration-200 ${
                                                      isSelected
                                                        ? 'bg-slate-700 text-white hover:bg-slate-800'
                                                        : 'bg-slate-800 text-white hover:bg-slate-700'
                                                    }`}
                                                  >
                                                    {isSelected ? '✗ Remove' : '+ Add Room'}
                                                  </button>
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Selected rooms — right drawer + floating reopen control */}
      {selectedRooms.length > 0 && (
        <>
          <button
            type="button"
            className={`fixed inset-0 z-[45] bg-black/40 transition-opacity duration-300 md:bg-black/20 ${
              selectionDrawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
            }`}
            onClick={() => setSelectionDrawerOpen(false)}
            aria-label="Close selection panel"
          />

          <aside
            id="hotel-selection-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="selection-drawer-title"
            className={`fixed right-0 z-[70] flex w-full max-w-md flex-col border-l border-gray-200 bg-white shadow-2xl transition-transform duration-300 ease-out top-16 sm:top-[4.75rem] bottom-0 h-auto max-h-[calc(100dvh-4rem)] sm:max-h-[calc(100dvh-4.75rem)] pb-[env(safe-area-inset-bottom)] ${
              selectionDrawerOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <div className="flex shrink-0 items-start justify-between gap-2 border-b border-gray-200 px-3 py-3 sm:px-4">
              <div className="min-w-0 pr-2">
                <h3
                  id="selection-drawer-title"
                  className="text-base font-light text-slate-800 typography-card-title tracking-wide"
                >
                  Selected ({selectedRooms.length})
                </h3>
                <p className="mt-0.5 text-[11px] leading-snug text-slate-500 font-poppinsLight">
                  Esc or backdrop to close
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <button
                  type="button"
                  onClick={clearAllSelections}
                  className="text-slate-700 hover:text-slate-900 text-xs font-medium underline-offset-2 hover:underline px-1"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => setSelectionDrawerOpen(false)}
                  className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                  aria-label="Close selection panel"
                >
                  <FaTimes className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-2 sm:px-4">
              <div className="space-y-2 pb-2">
                {selectedRooms.map((selection) => (
                  <div
                    key={selection.id}
                    className="rounded-lg border border-gray-200 bg-gray-50/90 p-2.5 sm:p-3"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start gap-1.5">
                          <span className="mt-0.5 shrink-0 scale-90 opacity-90 [&_svg]:h-3.5 [&_svg]:w-3.5">
                            {getRoomTypeIcon(selection.room.roomTypeName)}
                          </span>
                          <p className="text-xs font-medium text-slate-800 leading-snug line-clamp-2 sm:text-[13px]">
                            {selection.room.roomTypeName}
                            <span className="font-normal text-slate-500"> · </span>
                            <span className="font-normal text-slate-600">{selection.room.roomView}</span>
                          </p>
                        </div>
                        <p className="mt-1 line-clamp-1 text-[11px] text-slate-600 sm:text-xs">
                          {selection.ratePlan.ratePlanName}
                          <span className="text-slate-400"> · </span>
                          {selection.room.bedInfo}
                        </p>
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] sm:text-xs">
                          <span className="font-medium tabular-nums text-slate-900">
                            {hotelService.formatCurrency(selection.ratePlan.pricing.basePerNight, selection.ratePlan.pricing.currency)}
                            <span className="font-normal text-slate-500">/night</span>
                          </span>
                          <span className="text-slate-400">·</span>
                          <span className="tabular-nums text-slate-600">
                            {hotelService.formatCurrency(selection.ratePlan.pricing.base, selection.ratePlan.pricing.currency)} subtotal
                          </span>
                          <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold sm:text-[11px] ${
                            selection.ratePlan.refundable 
                              ? 'border border-emerald-200 bg-emerald-50 text-emerald-900' 
                              : 'border border-slate-300 bg-neutral-200 text-slate-800'
                          }`}>
                            {selection.ratePlan.refundable ? 'Refund' : 'Non-ref.'}
                          </span>
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1.5">
                        <span className="text-xs font-semibold tabular-nums text-slate-900 sm:text-sm">
                          {hotelService.formatCurrency(selection.ratePlan.pricing.base, selection.ratePlan.pricing.currency)}
                        </span>
                        <span className="text-[10px] text-slate-500">
                          {hotelService.calculateNights(searchCriteria.checkInDate, searchCriteria.checkOutDate)} nights
                        </span>
                        <button
                          type="button"
                          onClick={() => removeRoomSelection(selection.id)}
                          className="rounded-md bg-slate-800 px-2 py-1 text-[11px] font-medium text-white hover:bg-slate-900 sm:px-2.5 sm:text-xs"
                        >
                          Remove
                        </button>
                      </div>
                    </div>

                    {showMarkupBreakdown && (() => {
                      const base = selection.ratePlan.pricing?.base ?? 0;
                      const cur = selection.ratePlan.pricing?.currency || searchCriteria.currency || 'USD';
                      const floor = getRateCommissionFloor(selection.ratePlan);
                      const pct = getEffectiveMarkup(selection.id, selection.ratePlan);
                      const br = getAgentMarkupBreakdown(base, pct, true, { defaultCommissionPct: floor });
                      const incPct = getIncrementalPct(pct, floor);
                      const incAmt = getIncrementalAmount(base, pct, floor);
                      const pay = getYouPay(base, pct, floor);
                      const hasOverride = roomMarkupOverrides[selection.id] !== undefined;
                      return (
                        <div className="mt-2.5 border-t border-slate-200/80 pt-2.5">
                          <div className="relative group/drawerwrap">
                            {/* Tooltip */}
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 pointer-events-none opacity-0 group-hover/drawerwrap:opacity-100 transition-opacity duration-200">
                              <div className="bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl whitespace-nowrap">
                                <p className="font-semibold mb-0.5">Your commission</p>
                                <p className="text-slate-400 text-[11px]">Click to adjust your markup rate</p>
                              </div>
                              <div className="w-2.5 h-2.5 bg-slate-900 rotate-45 mx-auto -mt-1.5 rounded-sm" />
                            </div>

                          <button
                            type="button"
                            onClick={() => openCommissionPanel(selection.room, selection.ratePlan, selection.id)}
                            className="w-full rounded-xl overflow-hidden text-left transition group/drawercomm"
                            style={{ background: '#f1f5f9', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: '1px solid #cbd5e1', borderTop: `4px solid ${offMarketCommission ? '#d4af37' : '#1e293b'}` }}
                          >
                            <div className="px-2.5 pt-2.5 pb-2" style={{ background: '#f1f5f9' }}>
                              <div className="flex items-center justify-between gap-2 mb-1.5">
                                <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Your commission</span>
                                <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded border border-slate-300 bg-white text-slate-500">Advisors only</span>
                              </div>
                              {offMarketCommission && offMarketCommission.specialAdded > 0 && (
                                <div className="mb-1.5 rounded-xl border border-yellow-400/45 bg-gradient-to-r from-amber-50/95 via-yellow-50 to-amber-50/95 px-2 py-1.5 shadow-[0_2px_10px_rgba(212,175,55,0.1)] ring-1 ring-yellow-200/55">
                                  <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-500">Special commission</p>
                                  <p className="mt-0.5 text-[10px] font-medium leading-snug text-slate-800 tabular-nums">
                                    <span className="text-slate-600">{offMarketCommission.defaultBase}%</span>
                                    <span className="mx-0.5 text-slate-400">+</span>
                                    <span className="font-semibold text-yellow-600">{offMarketCommission.specialAdded}%</span>
                                    <span className="mx-1 text-slate-300">=</span>
                                    <span className="font-bold text-slate-900">{offMarketCommission.total}% floor</span>
                                  </p>
                                </div>
                              )}
                              <div className="rounded-lg border border-slate-100 divide-y divide-slate-100 overflow-hidden bg-white text-left">
                                <div className="flex justify-between items-center gap-2 px-2 py-1.5">
                                  <span className="text-[10px] text-slate-500">Base</span>
                                  <span className="text-[10px] font-semibold tabular-nums text-slate-700">{hotelService.formatCurrency(base, cur)}</span>
                                </div>
                                <div className="flex justify-between items-start gap-2 px-2 py-1.5">
                                  <div className="min-w-0">
                                    <span className="text-[10px] text-slate-500">Standard commission</span>
                                    <p className="text-[9px] text-slate-400 leading-tight">Not added to total below</p>
                                  </div>
                                  <span className="text-[10px] font-semibold tabular-nums text-slate-500 shrink-0">{hotelService.formatCurrency(br.defaultCommissionAmount, cur)}</span>
                                </div>
                                <div className="flex justify-between items-center gap-2 px-2 py-1.5">
                                  <span className="text-[10px] font-semibold text-slate-700">Total at {pct}% (your earnings)</span>
                                  <span className="text-[10px] font-bold tabular-nums text-slate-900">{hotelService.formatCurrency(br.totalCommissionAmount, cur)}</span>
                                </div>
                                {incPct > 0 && (
                                  <div className="flex justify-between items-center gap-2 px-2 py-1.5">
                                    <span className="text-[10px] text-slate-500">
                                      Added to price ({incPct}% on base above {floor.toFixed(1)}%)
                                    </span>
                                    <span className="text-[10px] font-semibold tabular-nums text-slate-700">+{hotelService.formatCurrency(incAmt, cur)}</span>
                                  </div>
                                )}
                                <div className="flex justify-between items-center gap-2 px-2 py-2 bg-slate-50">
                                  <span className="text-[10px] font-bold text-slate-800">You pay</span>
                                  <span className="text-sm font-black tabular-nums text-slate-900">{hotelService.formatCurrency(pay, cur)}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center justify-between px-2.5 py-2" style={{ background: offMarketCommission ? '#d4af37' : '#1e293b' }}>
                              <span className={`text-xs font-semibold tracking-wide ${offMarketCommission ? 'text-neutral-900' : 'text-slate-300'}`}>
                                {hasOverride ? 'Edit custom rate' : 'Adjust markup rate'}
                              </span>
                              <FaChevronRight className={`w-3 h-3 ${offMarketCommission ? 'text-neutral-800' : 'text-slate-400'}`} />
                            </div>
                          </button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
            </div>

            <div className="shrink-0 border-t border-gray-200 bg-white px-3 py-3 sm:px-4">
              <div className="flex flex-col gap-2">
                <div className="flex items-baseline justify-between gap-2">
                  <div>
                    <h4 className="text-sm font-light text-slate-800">
                      {'Subtotal'}
                    </h4>
                    <p className="text-[11px] text-slate-500">
                      {selectedRooms.length} room{selectedRooms.length > 1 ? 's' : ''} · {hotelService.calculateNights(searchCriteria.checkInDate, searchCriteria.checkOutDate)} nights
                      {showMarkupBreakdown && ' · base + optional markup add-ons'}
                    </p>
                  </div>
                  <p className="text-lg font-light tabular-nums text-slate-900 sm:text-xl">
                    {hotelService.formatCurrency(
                      showMarkupBreakdown
                        ? selectedRooms.reduce((total, sel) => {
                            const b = sel.ratePlan.pricing.base;
                            const f = getRateCommissionFloor(sel.ratePlan);
                            const p = getEffectiveMarkup(sel.id, sel.ratePlan);
                            return total + getYouPay(b, p, f);
                          }, 0)
                        : selectedRooms.reduce((total, selection) => total + selection.ratePlan.pricing.base, 0),
                      selectedRooms[0]?.ratePlan.pricing.currency || searchCriteria.currency
                    )}
                  </p>
                </div>
                {showMarkupBreakdown && (
                  <div className="flex justify-between gap-2 text-[11px] text-slate-500 border-t border-slate-100 pt-2">
                    <span>
                      Base subtotal{' '}
                      {hotelService.formatCurrency(
                        selectedRooms.reduce((t, s) => t + s.ratePlan.pricing.base, 0),
                        selectedRooms[0]?.ratePlan.pricing.currency || searchCriteria.currency
                      )}
                    </span>
                    <span className="text-amber-800 font-semibold tabular-nums">
                      Additional markup{' '}
                      {hotelService.formatCurrency(
                        selectedRooms.reduce((t, s) => {
                          const b = s.ratePlan.pricing.base;
                          const f = getRateCommissionFloor(s.ratePlan);
                          return t + getIncrementalAmount(b, getEffectiveMarkup(s.id, s.ratePlan), f);
                        }, 0),
                        selectedRooms[0]?.ratePlan.pricing.currency || searchCriteria.currency
                      )}
                    </span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={handlePriceCheckForAll}
                  disabled={priceCheckLoading}
                  className="w-full rounded-lg bg-slate-800 px-4 py-2.5 text-sm text-white transition-colors hover:bg-slate-700 disabled:opacity-50"
                >
                  {priceCheckLoading ? 'Checking Prices...' : 'Confirm Prices & Add to Cart'}
                </button>
              </div>
            </div>
          </aside>

          {!selectionDrawerOpen && (
            <button
              type="button"
              onClick={() => setSelectionDrawerOpen(true)}
              className="fixed bottom-5 right-4 z-40 flex max-w-[calc(100vw-2rem)] items-center gap-3 rounded-full border border-slate-200 bg-white py-3 pl-4 pr-5 shadow-xl transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 sm:bottom-6 sm:right-6"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white">
                {selectedRooms.length}
              </span>
              <span className="text-left text-sm font-medium text-slate-800">
                View selected
                <span className="block text-xs font-normal text-slate-500 tabular-nums">
                  {hotelService.formatCurrency(
                    showMarkupBreakdown
                      ? selectedRooms.reduce((t, s) => {
                          const b = s.ratePlan.pricing.base;
                          const f = getRateCommissionFloor(s.ratePlan);
                          return t + getYouPay(b, getEffectiveMarkup(s.id, s.ratePlan), f);
                        }, 0)
                      : selectedRooms.reduce((t, s) => t + s.ratePlan.pricing.base, 0),
                    selectedRooms[0]?.ratePlan.pricing.currency || searchCriteria.currency
                  )}
                  {showMarkupBreakdown && (
                    <span className="block text-[10px] text-teal-600/95 font-medium">
                      incl. optional markup add-ons
                    </span>
                  )}
                </span>
              </span>
            </button>
          )}
        </>
      )}

      {/* Multi-Room Confirmation Popup */}
      <ProfessionalConfirmationPopup
        isOpen={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        pendingSelections={pendingSelections}
        priceCheckResponses={priceCheckResponses}
        searchCriteria={searchCriteria}
        hotelData={hotelData}
        onAddToCart={handleAddAllToCart}
        addToCartLoading={addToCartLoading}
        emissionsData={emissionsData}
        setEmissionsData={setEmissionsData}
        emissionsLoading={emissionsLoading}
        setEmissionsLoading={setEmissionsLoading}
        emissionsError={emissionsError}
        setEmissionsError={setEmissionsError}
        agentMarkupSettings={agentMarkupSettings}
        roomMarkupOverrides={roomMarkupOverrides}
        offMarketProperty={offMarketProperty}
      />

      {/* ── Commission Markup Side Panel ─────────────────────────────────── */}
      {commissionPanel.open && (() => {
        const { ratePlan, selectionId } = commissionPanel;
        const panelFloor = getRateCommissionFloor(ratePlan);
        const total = ratePlan?.pricing?.base ?? 0;
        const currency = ratePlan?.pricing?.currency || searchCriteria?.currency || 'USD';
        const br = getAgentMarkupBreakdown(total, panelTempMarkup, true, {
          defaultCommissionPct: panelFloor
        });

        /** Only the agent's markup ABOVE the rate-source floor hits the customer price. */
        const panelIncrementalPct = getIncrementalPct(panelTempMarkup, panelFloor);
        const panelIncrementalAmount = Math.round(total * (panelIncrementalPct / 100) * 100) / 100;
        const panelYouPay = Math.round((total + panelIncrementalAmount) * 100) / 100;

        return (
          <>
            {/* Backdrop */}
            <button
              type="button"
              aria-label="Close commission panel"
              className="fixed inset-0 z-[80] bg-black/40 cursor-default"
              onClick={closeCommissionPanel}
            />

            {/* Panel */}
            <aside
              role="dialog"
              aria-modal="true"
              aria-label="Adjust markup rate"
              className="fixed right-0 top-16 bottom-0 z-[90] w-full max-w-sm bg-white shadow-2xl border-l border-gray-200 flex flex-col overflow-hidden"
            >
              {/* Header */}
              <div className={`flex items-start justify-between px-6 pt-6 pb-5 border-b border-gray-200 ${offMarketCommission ? 'bg-gradient-to-br from-amber-50/90 via-yellow-50/60 to-white' : 'bg-gray-50'}`}>
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-1">Advisors only · Commission settings</p>
                  <h2 className="text-lg font-bold text-slate-900 leading-snug">
                    {offMarketCommission
                      ? <>Special commission<br />on this stay</>
                      : <>Increase your commission<br />with a markup rate</>}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={closeCommissionPanel}
                  className="p-2 rounded-xl text-gray-400 hover:text-gray-900 hover:bg-gray-200 transition-all -mt-1 -mr-1"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>

              {/* Scrollable body */}
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5 bg-white">
                {/* Description */}
                {offMarketCommission ? (
                  <div className="rounded-xl border border-yellow-400/40 bg-gradient-to-br from-amber-50/90 via-yellow-50/80 to-amber-50/90 px-4 py-3 space-y-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)] ring-1 ring-yellow-200/50">
                    <p className="text-sm text-slate-700 leading-relaxed">
                      <span className="font-semibold text-slate-900">Special commission</span> from this property.
                      {offMarketCommission.specialAdded > 0 ? (
                        <>
                          {' '}Minimum <span className="font-bold tabular-nums text-slate-900">{offMarketCommission.total}%</span>
                          {' '}(<span className="tabular-nums text-slate-600">{offMarketCommission.defaultBase}%</span>
                          <span className="text-slate-400"> + </span>
                          <span className="tabular-nums font-semibold text-yellow-600">{offMarketCommission.specialAdded}%</span>).
                        </>
                      ) : (
                        <>
                          {' '}Minimum <span className="font-bold tabular-nums text-slate-900">{offMarketCommission.total}%</span> for this booking.
                        </>
                      )}
                    </p>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      Only percentage points <span className="font-semibold text-slate-700">above {Number(offMarketCommission.total).toFixed(1)}%</span> increase the total you pay. Commission earnings are shared monthly via your My Commissions report.
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 leading-relaxed">
                    <span className="font-semibold text-slate-800">Standard commission</span> is shown for reference and is <span className="font-semibold text-slate-800">not</span> added to the total you pay. Only percentage points <span className="font-semibold text-slate-800">above {panelFloor.toFixed(1)}%</span> increase the payable total. Commission earnings are shared monthly via your My Commissions report.
                  </p>
                )}

                {/* Rate plan badge */}
                {ratePlan?.ratePlanName && (
                  <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                    <svg className="w-3.5 h-3.5 shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>
                    <span className="font-medium text-slate-700">{ratePlan.ratePlanName}</span>
                    <span className="ml-auto font-bold text-slate-900 tabular-nums">{hotelService.formatCurrency(total, currency)}</span>
                  </div>
                )}

                {/* Off-market commission breakdown strip */}
                {offMarketCommission && (
                  <div className="rounded-xl border border-yellow-400/40 divide-y divide-yellow-100/80 overflow-hidden bg-white shadow-sm shadow-amber-500/5 ring-1 ring-yellow-200/40">
                    <div className="flex justify-between items-center px-4 py-2.5 bg-gradient-to-r from-amber-50/90 to-yellow-50/80">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">Special commission</span>
                    </div>
                    <div className="flex justify-between items-center px-4 py-2">
                      <span className="text-xs text-slate-600">Base</span>
                      <span className="text-xs font-semibold tabular-nums text-slate-700">{offMarketCommission.defaultBase.toFixed(1)}%</span>
                    </div>
                    {offMarketCommission.specialAdded > 0 && (
                      <div className="flex justify-between items-center px-4 py-2">
                        <span className="text-xs font-medium text-slate-700">Add-on</span>
                        <span className="text-xs font-bold tabular-nums text-yellow-600">+{offMarketCommission.specialAdded.toFixed(1)}%</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center px-4 py-2.5 bg-gradient-to-r from-amber-50/80 to-yellow-50/70 font-semibold">
                      <span className="text-xs text-slate-800">Floor (slider min)</span>
                      <span className="text-sm font-bold tabular-nums text-slate-900">{offMarketCommission.total.toFixed(1)}%</span>
                    </div>
                  </div>
                )}

                {/* Markup rate stepper */}
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 shadow-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-700">Markup rate — your earnings</span>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setPanelTempMarkup((v) => adjustPanelMarkup(v, -STEP, panelFloor))}
                        disabled={panelTempMarkup <= panelFloor}
                        className="w-8 h-8 rounded-full border-2 border-slate-300 flex items-center justify-center text-slate-600 hover:border-slate-500 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed transition-all bg-white"
                        aria-label="Decrease markup"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" /></svg>
                      </button>
                      <span className="text-lg font-bold text-slate-900 tabular-nums w-14 text-center">
                        {panelTempMarkup.toFixed(1)}%
                      </span>
                      <button
                        type="button"
                        onClick={() => setPanelTempMarkup((v) => adjustPanelMarkup(v, STEP, panelFloor))}
                        disabled={panelTempMarkup >= effectiveMaxMarkup}
                        className="w-8 h-8 rounded-full border-2 border-slate-800 bg-slate-800 flex items-center justify-center text-white hover:bg-slate-900 hover:border-slate-900 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
                        aria-label="Increase markup"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                      </button>
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1.5">
                    Range: {panelFloor.toFixed(1)}%{offMarketCommission ? '' : ` – ${effectiveMaxMarkup}%`} · steps of {STEP}%
                    {offMarketCommission && (
                      <span className="ml-1 font-medium text-slate-500">(min: special commission floor)</span>
                    )}
                  </p>
                </div>

                {/* Breakdown box */}
                <div className="rounded-xl border border-slate-200 divide-y divide-slate-200 overflow-hidden bg-white shadow-sm">
                  <div className="px-4 py-3 bg-slate-50">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-0.5">Total commission at {panelTempMarkup.toFixed(1)}% (your earnings)</p>
                    <p className="text-xl font-bold text-slate-900 tabular-nums">
                      {hotelService.formatCurrency(br.totalCommissionAmount, currency)}
                    </p>
                    {offMarketCommission && offMarketCommission.specialAdded > 0 && (
                      <p className="text-[10px] text-slate-600 mt-0.5 tabular-nums">
                        {offMarketCommission.defaultBase}% + {offMarketCommission.specialAdded}% = {offMarketCommission.total}%
                      </p>
                    )}
                  </div>
                  {panelIncrementalPct > 0 && (
                    <div className="px-4 py-3 bg-white">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-0.5">
                        Added to price ({panelIncrementalPct}% on base above {panelFloor.toFixed(1)}%)
                      </p>
                      <p className="text-lg font-bold text-slate-800 tabular-nums">
                        +{hotelService.formatCurrency(panelIncrementalAmount, currency)}
                      </p>
                    </div>
                  )}
                  <div className="px-4 py-3 bg-white">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-0.5">
                      Total you pay (base{panelIncrementalPct > 0 ? ` + add-on above ${panelFloor.toFixed(1)}%` : ''})
                    </p>
                    <p className="text-xl font-bold text-slate-900 tabular-nums">
                      {hotelService.formatCurrency(panelYouPay, currency)}
                    </p>
                  </div>
                  <div className="px-4 py-3 bg-gray-50">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-0.5">Base rate</p>
                    <p className="text-sm font-semibold text-slate-700 tabular-nums">
                      {hotelService.formatCurrency(total, currency)}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-1">Standard commission: {hotelService.formatCurrency(br.defaultCommissionAmount, currency)} — not added to total above</p>
                  </div>
                </div>

                {/* Reset to global hint */}
                {roomMarkupOverrides[selectionId] !== undefined && (
                  <button
                    type="button"
                    onClick={() => {
                      setPanelTempMarkup(Math.max(panelFloor, getContextDefaultMarkup(ratePlan)));
                    }}
                    className="text-xs text-slate-500 underline underline-offset-2 hover:text-slate-800 transition-colors font-medium"
                  >
                    Reset to default ({Math.max(panelFloor, getContextDefaultMarkup(ratePlan)).toFixed(1)}%)
                  </button>
                )}

                <p className="text-[11px] text-slate-400 leading-relaxed">
                  This adjustment applies only to this rate plan during your current session. Your global default remains unchanged.
                </p>
              </div>

              {/* Footer actions */}
              <div className="shrink-0 flex items-center gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
                <button
                  type="button"
                  onClick={closeCommissionPanel}
                  className="flex-1 py-3 rounded-xl border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors bg-white"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveCommissionPanel}
                  className="flex-1 py-3 rounded-xl bg-slate-900 text-sm font-bold text-white hover:bg-slate-800 transition-colors shadow-sm"
                >
                  Save
                </button>
              </div>
            </aside>
          </>
        );
      })()}

    </div>
  );
};

export default RoomExplorerSelection;